/**
 * Maintenance plan & trigger types — frontend mirror of the
 * Phase 3d backend (maintenance-plans + plan-triggers modules).
 *
 * A plan is a TEMPLATE for recurring Work Orders. When a trigger
 * fires, the backend's scheduler generates a brand-new WO that
 * COPIES the plan's fields at that moment. Plan edits never
 * touch already-generated WOs — that's how Phase 3c Ed25519
 * signatures stay valid even when the plan changes.
 *
 * A plan can target either:
 *   - a single equipment instance (equipmentId set), OR
 *   - an equipment TYPE (equipmentTypeId set), generating WOs for
 *     every equipment of that type — optionally restricted to one
 *     vessel via vesselId.
 *
 * Triggers come in three kinds:
 *   time     — fire every N days, anchored to anchorDate
 *   counter  — fire every N units of a measurement
 *              (e.g. running_hours +500h)
 *   cbm      — fire when the latest measurement matches a
 *              threshold via an operator (gt/gte/lt/lte/approx_eq)
 */

export type WorkOrderType =
  | 'preventive'
  | 'corrective'
  | 'condition_based'
  | 'drydock'
  | 'improvement'
  | 'inspection';
export type WorkOrderPriority = 'low' | 'normal' | 'high' | 'critical';

export interface MaintenancePlan {
  id: string;
  companyId: string;

  /** Target: single equipment OR equipment type. Mutually exclusive. */
  equipmentId: string | null;
  equipmentTypeId: string | null;
  /** Only meaningful when equipmentTypeId is set; narrows generation
   *  to one vessel. NULL = company-wide. */
  vesselId: string | null;

  // Template fields copied into generated WOs
  title: string;
  description: string | null;
  type: WorkOrderType;
  priority: WorkOrderPriority;
  estimatedHours: string | null;

  /** Days before the trigger is due to actually generate the WO
   *  (gives time to order parts, schedule technicians). */
  leadTimeDays: number;

  isActive: boolean;
  lastGeneratedAt: string | null;

  createdByUserId: string;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

export const PLAN_TRIGGER_KINDS = ['time', 'counter', 'cbm'] as const;
export type PlanTriggerKind = (typeof PLAN_TRIGGER_KINDS)[number];

export const CBM_OPERATORS = ['gt', 'gte', 'lt', 'lte', 'approx_eq'] as const;
export type CbmOperator = (typeof CBM_OPERATORS)[number];

export interface PlanTrigger {
  id: string;
  planId: string;
  kind: PlanTriggerKind;

  // time fields
  intervalDays: number | null;
  anchorDate: string | null;

  // counter fields
  counterField: string | null;
  intervalValue: string | null;
  lastFiredValue: string | null;

  // cbm fields
  cbmOperator: CbmOperator | null;
  cbmThreshold: string | null;

  // common state
  lastFiredAt: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

/** Color tokens for the trigger-kind badge in the UI. */
export function planTriggerKindColor(kind: PlanTriggerKind): string {
  switch (kind) {
    case 'time':
      return 'bg-sky-100 text-sky-700';
    case 'counter':
      return 'bg-purple-100 text-purple-700';
    case 'cbm':
      return 'bg-amber-100 text-amber-700';
  }
}

/** Pretty-print a trigger schedule in plain text for the list and
 *  detail summaries. The full structured view is only on the trigger
 *  card itself. */
export function describeTrigger(trigger: PlanTrigger): string {
  if (trigger.kind === 'time' && trigger.intervalDays != null) {
    return `every ${trigger.intervalDays}d`;
  }
  if (
    trigger.kind === 'counter' &&
    trigger.counterField &&
    trigger.intervalValue
  ) {
    return `every ${trigger.intervalValue} on ${trigger.counterField}`;
  }
  if (
    trigger.kind === 'cbm' &&
    trigger.counterField &&
    trigger.cbmOperator &&
    trigger.cbmThreshold
  ) {
    const op = {
      gt: '>',
      gte: '≥',
      lt: '<',
      lte: '≤',
      approx_eq: '≈',
    }[trigger.cbmOperator];
    return `${trigger.counterField} ${op} ${trigger.cbmThreshold}`;
  }
  return '—';
}
